import React from 'react';
import {createRoot} from 'react-dom/client';
import axios from 'axios';
import IllegalArgumentError from '../domain/illegal-argument-error';
import queryParameterUtils from '../utils/query-parameter-utils';
import ErrorBox from '../components/common/error-box';

const determineEnv = () => {
  const hostname = window.location.hostname;
  if (hostname === 'local.acerill-staging.co.uk') {
    return 'local';
  } else if (hostname.indexOf('acerill-staging.co.uk') >= 0) {
    return 'staging';
  } else {
    return 'production';
  }
};
const env = determineEnv();
export {env};

const config = (env === 'local' || env === 'staging') ?
  {
    loginUrl: 'https://auth.acerill-staging.co.uk',
    clientUrl: 'https://custom-forms-client.acerill-staging.co.uk',
  } :
  {
    loginUrl: 'https://auth.acerill.com',
    clientUrl: 'https://custom-forms-client.acerill.com',
  };
export {config};

const apiUrl = env === 'production' ?
  'https://api.acerill.com' :
  'https://api.acerill-staging.co.uk';
export {apiUrl};

const orgId = queryParameterUtils.tryFindQueryParameter('shop');
const aceCustId = orgId;
export {orgId, aceCustId};

const platform = orgId && orgId.indexOf('.myshopify.com') > 0 ?
  'SHOPIFY' :
  null;
export {platform};

const shouldSendErrorToServer = err => {
  if (err instanceof IllegalArgumentError) {
    return false;
  }
  const lowerCaseErrorMessage = (err.message || '').toLowerCase();
  if (lowerCaseErrorMessage === 'network error') {
    return false;
  } else if (lowerCaseErrorMessage === '' && ((err.error || {}).code || '') === 'ECONNABORTED') {
    return false;
  }
  return true;
};
const removeStackAndFunctionsSafely = err => {
  try {
    const result = {};
    for (let entry of Object.entries(err).filter(e => e[0] !== 'stack' && typeof e[1] !== 'function')) {
      result[entry[0]] = entry[1];
    }
    return result;
  } catch (ignore) {
    return null;
  }
};
const sendErrorToServer = err => {
  return axios.post(
    apiUrl + '/monitoring/v1/public/client-errors',
    {
      product: 'custom-forms',
      aceCustId: aceCustId,
      clientCode: 'admin-v2',
      message: err.message,
      error: removeStackAndFunctionsSafely(err),
      stack: err.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    }
  );
};
const errorBoxContainer = document.createElement('div');
const bodyElement = document.getElementsByTagName('body')[0];
bodyElement.insertBefore(errorBoxContainer, bodyElement.firstChild);
const errorBoxRoot = createRoot(errorBoxContainer);
const defaultErrorHandler = err => {
  console.log('ERROR: ' + (err.stack || JSON.stringify(err)));
  if (shouldSendErrorToServer(err)) {
    sendErrorToServer(err)
      .catch(err => console.log(err.stack || JSON.stringify(err)));
  }
  const errorBox = <ErrorBox text={err.message || 'Sorry, an error has occurred'}
                             position="fixed"
                             onClose={() => errorBoxRoot.unmount()}/>;
  errorBoxRoot.render(errorBox);
};
export {defaultErrorHandler};

const Context = React.createContext({
  env,
  aceCustId,
  apiUrl,
  defaultErrorHandler,
});
export {Context};
